/**
 * App-takeover manifest types.
 *
 * IMPORTANT: This module and every `takeovers/<id>/manifest.ts` MUST stay
 * Node-loadable. Do not import React, React Native, app aliases, components, or
 * assets here. Runtime-only values are represented by the references below.
 */

export type TakeoverStatus = 'draft' | 'active' | 'retired';
export type TakeoverRegion = 'US' | 'CA';
export type AssetStatus = 'final' | 'placeholder';
export type TakeoverSurface =
  | 'splash'
  | 'menuIcon'
  | 'shp'
  | 'menuSections'
  | 'pdp'
  | 'cart'
  | 'paymentProcessing'
  | 'orderConfirm';

/** Approved app-owned primitive tokens that a Node-loadable manifest may use. */
export const TAKEOVER_DESIGN_TOKENS = [
  '$bbqBrown',
  '$greyFive',
  '$greyThree',
  '$mayoEggWhite',
  '$white',
] as const;

export type TakeoverDesignToken = typeof TAKEOVER_DESIGN_TOKENS[number];

export interface TakeoverAsset {
  /** Path relative to the takeover folder, e.g. `assets/menu-icon.webp`. */
  path: string;
  /** Human-readable purpose used in validation and the generated QA checklist. */
  role: string;
  /** Placeholders are permitted only while the manifest status is `draft`. */
  status: AssetStatus;
}

export interface TakeoverDeletion {
  notes?: string;
}

export interface TakeoverAssetReference<Key extends string = string> {
  readonly kind: 'asset';
  readonly key: Key;
}

export interface TakeoverComponentReference<Key extends string = string, Props = unknown> {
  readonly kind: 'component';
  readonly key: Key;
  /** Serializable props resolved once and injected by the runtime builder. */
  readonly props?: Props;
}

export interface TakeoverDesignTokenReference {
  readonly kind: 'token';
  readonly key: TakeoverDesignToken;
}

export type TakeoverRuntimeReference =
  | TakeoverAssetReference
  | TakeoverComponentReference
  | TakeoverDesignTokenReference;

export const asset = <Key extends string>(key: Key): TakeoverAssetReference<Key> => ({
  kind: 'asset',
  key,
});

export function component<Key extends string>(key: Key): TakeoverComponentReference<Key, undefined>;
export function component<Key extends string, Props>(
  key: Key,
  props: Props
): TakeoverComponentReference<Key, Props>;
export function component<Key extends string, Props>(
  key: Key,
  props?: Props
): TakeoverComponentReference<Key, Props | undefined> {
  return {
    kind: 'component',
    key,
    ...(props === undefined ? {} : { props }),
  };
}

export const token = (key: TakeoverDesignToken): TakeoverDesignTokenReference => ({
  kind: 'token',
  key,
});

export type TakeoverColor = string | TakeoverDesignTokenReference;

export interface TakeoverActionButtonConfig {
  backgroundColor?: TakeoverColor;
  borderColor?: TakeoverColor;
  borderWidth?: number;
  _text?: {
    color?: TakeoverColor;
    fontFamily?: string;
  };
  _pressed?: {
    backgroundColor?: TakeoverColor;
  };
  _hover?: {
    backgroundColor?: TakeoverColor;
  };
  _focus?: {
    backgroundColor?: TakeoverColor;
  };
}

export interface TakeoverRadialGradientConfig {
  center?: { x: number; y: number };
  radius?: { x: number; y: number };
  stops: {
    color: TakeoverColor;
    offset: number;
    opacity?: number;
  }[];
}

export type TakeoverStyle = Readonly<Record<string, string | number | boolean | null | undefined>>;

export interface TakeoverMenuSectionConfig<AssetKey extends string = string> {
  headerBackgroundColor?: TakeoverColor;
  headerBackgroundImage?: TakeoverAssetReference<AssetKey>;
  itemBackgroundColor?: TakeoverColor;
  footerImage?: TakeoverAssetReference<AssetKey>;
  footerImageOffset?: number;
  titleMaxWidth?: number;
  sectionTileBackgroundImage?: TakeoverAssetReference<AssetKey>;
  sectionTileImage?: TakeoverAssetReference<AssetKey>;
  sectionTileImageStyle?: TakeoverStyle;
  fontColor?: TakeoverColor;
  transitionPicture?: TakeoverAssetReference<AssetKey>;
  transitionAudio?: TakeoverAssetReference<AssetKey>;
  transitionPictureTime?: number;
  transitionPictureAudio?: TakeoverAssetReference<AssetKey>;
}

export interface TakeoverPdpCommonConfig<AssetKey extends string = string> {
  overviewTopImage?: TakeoverAssetReference<AssetKey>;
  overviewBottomImage?: TakeoverAssetReference<AssetKey>;
  overviewBottomImageOffset?: number;
}

export type TakeoverProductWizardStep =
  | 'ProductHome'
  | 'ProductCustomization'
  | 'PickerAspect'
  | 'PickerCustomization'
  | 'PickerCustomizationSides'
  | 'PickerCustomizationDrinks'
  | 'ProductReview';

export interface TakeoverPdpItemConfig<AssetKey extends string = string>
  extends TakeoverPdpCommonConfig<AssetKey> {
  sectionFontColor?: TakeoverColor;
  sectionBackgroundColor?: TakeoverColor;
  sectionBackgroundImage?: TakeoverAssetReference<AssetKey>;
  cartItemImage?: TakeoverAssetReference<AssetKey>;
  cartItemBackgroundImage?: TakeoverAssetReference<AssetKey>;
  audio?: TakeoverAssetReference<AssetKey>;
  additionalAudioSteps?: Partial<
    Record<TakeoverProductWizardStep, TakeoverAssetReference<AssetKey>>
  >;
  headerBackgroundColor?: TakeoverColor;
  headerFontColor?: TakeoverColor;
}

export interface TakeoverTargetGroup<Config> {
  ids: readonly string[];
  config: Config;
}

export interface TakeoverDeclarativeConfig<
  AssetKey extends string = string,
  ComponentKey extends string = string
> {
  splashScreen?: {
    lottie?: TakeoverAssetReference<AssetKey>;
    audio?: TakeoverAssetReference<AssetKey>;
    video?: TakeoverAssetReference<AssetKey>;
    durationMs?: number;
  };
  menuIcon?: TakeoverAssetReference<AssetKey>;
  menuIconActive?: TakeoverAssetReference<AssetKey>;
  fontFamily?: string;
  primaryActionButton?: TakeoverActionButtonConfig;
  primaryActionButtonRadialGradient?: TakeoverRadialGradientConfig | null;
  homePage?: {
    audio?: TakeoverAssetReference<AssetKey>;
    targetSimpleHomePageIds?: readonly string[];
    containerBackgroundColor?: TakeoverColor;
    videoHasAudio?: boolean;
    primaryButton?: TakeoverActionButtonConfig;
    deliveryButton?: TakeoverActionButtonConfig;
    muteButtonCoachmarkArgs?: {
      message: string;
      imageSource: TakeoverAssetReference<AssetKey>;
    } | null;
    showReplayButton?: boolean;
    HeroBottomGradient?: TakeoverComponentReference<ComponentKey> | null;
  };
  menu?: {
    audio?: TakeoverAssetReference<AssetKey>;
    targetSectionGroups: readonly TakeoverTargetGroup<TakeoverMenuSectionConfig<AssetKey>>[];
  };
  pdp?: TakeoverPdpCommonConfig<AssetKey> & {
    targetMenuItemGroups: readonly TakeoverTargetGroup<TakeoverPdpItemConfig<AssetKey>>[];
    backgroundImage?: TakeoverAssetReference<AssetKey>;
    summaryPageBackgroundImage?: TakeoverAssetReference<AssetKey>;
    summaryPageTextColor?: TakeoverColor;
    backgroundColor?: TakeoverColor;
    priceAndCaloriesFontColor?: TakeoverColor;
    headerBackgroundColor?: TakeoverColor;
    HeaderFlair?: TakeoverComponentReference<ComponentKey> | null;
    headerFontColor?: TakeoverColor;
  };
  cart?: {
    audio?: TakeoverAssetReference<AssetKey>;
    paymentProcessingVideo?: TakeoverAssetReference<AssetKey>;
    paymentProcessingBackgroundColor?: TakeoverColor;
  };
  orderConfirmDetails?: {
    headerBackgroundColor?: TakeoverColor;
    headerVideo?: TakeoverAssetReference<AssetKey>;
    headerImage?: TakeoverAssetReference<AssetKey>;
    ConfirmHeaderOverlay?: TakeoverComponentReference<ComponentKey> | null;
    headerVideoAspectRatio?: number;
    headerFontColor?: TakeoverColor;
  };
}

export interface TakeoverManifest<
  Assets extends Record<string, TakeoverAsset> = Record<string, TakeoverAsset>,
  Config extends TakeoverDeclarativeConfig<
    Extract<keyof Assets, string>,
    string
  > = TakeoverDeclarativeConfig<Extract<keyof Assets, string>, string>
> {
  id: string;
  ldName: string;
  status: TakeoverStatus;
  regions: readonly TakeoverRegion[];
  displayName: string;
  assets: Assets;
  config: Config;
  deletion?: TakeoverDeletion;
}

/** Preserve literal asset/component keys while checking the declarative shape. */
export const defineTakeoverManifest = <
  Assets extends Record<string, TakeoverAsset>,
  Config extends TakeoverDeclarativeConfig<Extract<keyof Assets, string>, string>
>(
  manifest: TakeoverManifest<Assets, Config>
): TakeoverManifest<Assets, Config> => manifest;

type ReferenceKeys<Value, Kind extends TakeoverRuntimeReference['kind']> = Value extends {
  readonly kind: Kind;
  readonly key: infer Key;
}
  ? Extract<Key, string>
  : Value extends readonly (infer Item)[]
  ? ReferenceKeys<Item, Kind>
  : Value extends object
  ? { [Key in keyof Value]: ReferenceKeys<Value[Key], Kind> }[keyof Value]
  : never;

export type TakeoverManifestAssetKey<Manifest extends TakeoverManifest> = Extract<
  ReferenceKeys<Manifest['config'], 'asset'>,
  string
>;

export type TakeoverManifestComponentKey<Manifest extends TakeoverManifest> = Extract<
  ReferenceKeys<Manifest['config'], 'component'>,
  string
>;

export type TakeoverBindingRecord<Key extends string, Value> = [Key] extends [never]
  ? Record<string, never>
  : { [BindingKey in Key]: Value };
